import {useTranslation} from "react-i18next";
import {faArrowRight, faHeadphones} from "@fortawesome/pro-solid-svg-icons";

import {Styled} from "../MainPage.styled";
import ActionButton from "../../../lib/UIBookBrd/ActionButton";
import useS115 from "../../../lib/Hooks/useS115";
import {RENT_LIST_PAGE, STUDIO_PAGE} from "../../../Routes";
import {Contact__Values} from "../../../PageValues";

type HeroProps = {
    /** Looping studio clip used as the hero backdrop. */
    videoSrc?: string
    /** Fallback still for browsers that refuse autoplay. */
    imageSrc?: string
}

const EASE = [0.16, 1, 0.3, 1]

const fadeUp = (delay: number) => ({
    initial: {opacity: 0, y: 26},
    animate: {opacity: 1, y: 0},
    transition: {duration: 0.75, delay, ease: EASE}
})

const Hero = (
    {
        videoSrc,
        imageSrc
    }: HeroProps) => {

    const {t} = useTranslation()

    const {navigatePage} = useS115()

    const titleLines = t('lbl_hero_title').split('<BREAK/>')

    /* Only claims that actually mean something to someone renting gear. */
    const stats = [
        {value: Contact__Values.city, label: t('lbl_stat_base')}
    ]

    return (
        <Styled.Hero>
            <Styled.HeroMedia>
                {videoSrc ? (
                    <Styled.HeroVideo
                        autoPlay
                        loop
                        muted
                        playsInline
                        poster={imageSrc}
                    >
                        <source src={videoSrc} type={'video/mp4'}/>
                    </Styled.HeroVideo>
                ) : (
                    <Styled.HeroImage
                        src={imageSrc}
                        alt={''}
                        grayscale={true}
                    />
                )}
            </Styled.HeroMedia>
            <Styled.HeroScrim/>

            <Styled.HeroInner>
                <Styled.Eyebrow as={'span'}>
                    {t('lbl_hero_eyebrow')}
                </Styled.Eyebrow>

                <Styled.HeroTitle>
                    {titleLines.map((line, index) => (
                        <Styled.HeroTitleLine
                            key={line}
                            initial={{opacity: 0, y: 40}}
                            animate={{opacity: 1, y: 0}}
                            transition={{duration: 0.9, delay: 0.08 * index, ease: EASE}}
                        >
                            {index === titleLines.length - 1 ? (
                                <Styled.HeroAccent>{line}</Styled.HeroAccent>
                            ) : line}
                        </Styled.HeroTitleLine>
                    ))}
                </Styled.HeroTitle>

                <Styled.HeroLead {...fadeUp(0.22)}>
                    {t('lbl_hero_lead')}
                </Styled.HeroLead>

                <Styled.HeroActions {...fadeUp(0.3)}>
                    <ActionButton
                        size={'lg'}
                        label={t('lbl_hero_cta_primary')}
                        iconRight={faArrowRight}
                        onClick={() => navigatePage(RENT_LIST_PAGE)}
                    />
                    <ActionButton
                        size={'lg'}
                        variant={'outline'}
                        label={t('lbl_hero_cta_secondary')}
                        icon={faHeadphones}
                        onClick={() => navigatePage(STUDIO_PAGE)}
                    />
                </Styled.HeroActions>

                <Styled.HeroStats {...fadeUp(0.38)}>
                    {stats.map((stat) => (
                        <Styled.HeroStat key={stat.label}>
                            <Styled.HeroStatValue>{stat.value}</Styled.HeroStatValue>
                            <Styled.HeroStatLabel>{stat.label}</Styled.HeroStatLabel>
                        </Styled.HeroStat>
                    ))}
                </Styled.HeroStats>
            </Styled.HeroInner>

            <Styled.ScrollCue>
                <Styled.ScrollCueLabel>{t('lbl_scroll')}</Styled.ScrollCueLabel>
                <Styled.ScrollCueLine/>
            </Styled.ScrollCue>
        </Styled.Hero>
    )
}

export default Hero
